import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SkynetIcon from '../../Fundamentals/SkynetIcon';
import { useOutsideClick } from '../../Fundamentals/_hooks/useOutsideClick';
import { getInvoiceRowActions } from './invoiceRowActionsVisibility';
import type { TransactionRow } from '../../../Containers/Customer/CustomerTransactions.types';

interface InvoiceRowActionsProps {
  row: TransactionRow;
  isInvoiceAdmin: boolean;
  onViewDetails: (row: TransactionRow) => void;
  onSendCopy: (row: TransactionRow) => void;
}

const InvoiceRowActions = ({
  row,
  isInvoiceAdmin,
  onViewDetails,
  onSendCopy,
}: InvoiceRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);
  useOutsideClick(open, containerRef, containerRef, close);

  const { sendCopyDisabled, showDelete, showRegisterRepayment, showCredit } = getInvoiceRowActions(
    row,
    isInvoiceAdmin
  );

  return (
    <div
      className={`dropdown d-inline-block m-0 transactions-kebab${open ? ' open' : ''}`}
      ref={containerRef}
      onClick={(event) => event.stopPropagation()}
    >
      <SkynetIcon
        icon="vismaicon-sm vismaicon-more"
        title={t('customertransactions.rowActions') ?? undefined}
        onClick={() => setOpen((prev) => !prev)}
      />
      {open && (
        <ul
          className="dropdown-menu dropdown-menu-right"
          style={{ width: 'max-content', right: 0, left: 'auto' }}
        >
          <li>
            <a
              onClick={() => {
                onViewDetails(row);
                close();
              }}
            >
              {t('customertransactions.viewDetails')}
            </a>
          </li>
          <li className={sendCopyDisabled ? 'disabled' : ''}>
            <a
              onClick={
                sendCopyDisabled
                  ? undefined
                  : () => {
                      onSendCopy(row);
                      close();
                    }
              }
            >
              {t('customertransactions.sendCopy')}
            </a>
          </li>
          {showRegisterRepayment && (
            <li>
              <a onClick={close}>{t('customertransactions.registerRepayment')}</a>
            </li>
          )}
          {showCredit && (
            <li>
              <a onClick={close}>{t('customertransactions.credit')}</a>
            </li>
          )}
          {showDelete && (
            <li>
              <a onClick={close}>{t('customertransactions.deleteUninvoiced')}</a>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default InvoiceRowActions;
